import StyledButton from '@/components/styled/StyledButton';
import StyledCheckboxGroup from '@/components/styled/StyledCheckboxGroup';
import StyledInput from '@/components/styled/StyledInput';
import StyledTooltipContainer from '@/components/styled/StyledTooltipContainer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { ExtendedCarCategoriesFormData } from '@/types/benefits/carRental/CarCategoryFormData';
import {
  ArrayPath,
  Control,
  FieldArray,
  Path,
  PathValue,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import PlusIcon from '@/assets/icons/plus.svg?react';
import TrashIcon from '@/assets/icons/trash-02.svg?react';
import CopyIcon from '@/assets/icons/copy-03.svg?react';
import { addDays } from 'date-fns';
import clsx from 'clsx';
import StyledDatePicker from '@/components/styled/StyledDatePicker';
import { WEEKDAYS, WEEKDAYS_TRANSLATED } from '@/constants/weekdays';
import { TarificationSchema } from '@/types/benefits/tarification/TarificationFormData';
import { useCallback, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';

interface CategoryTarifListProps<
  FieldsType extends ExtendedCarCategoriesFormData,
> {
  control: Control<FieldsType>;
  index: number;
  isViewMode?: boolean;
}

const CategoryTarifList = <FieldsType extends ExtendedCarCategoriesFormData>({
  control,
  index,
  isViewMode = false,
}: CategoryTarifListProps<FieldsType>) => {
  const { t } = useTranslation();
  const { resetField } = useFormContext<FieldsType>();
  const createDefaultTarif = (applicationDays: string[] = WEEKDAYS) =>
    ({
      name: '',
      periods: [
        {
          startDate: undefined,
          endDate: undefined,
          applicationDays,
        },
      ],
      pricePartitions: [
        {
          price: undefined,
          from: 1,
          to: 1,
          specialPrice: undefined,
        },
      ],
    }) as FieldArray<FieldsType, ArrayPath<FieldsType>>[][0];

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: `carCategories.${index}.tarifs` as ArrayPath<FieldsType>,
  });
  const tarifs = useWatch({
    control,
    name: `carCategories.${index}.tarifs` as Path<FieldsType>,
  }) as ExtendedCarCategoriesFormData['carCategories'][number]['tarifs'];

  const appendTarif = useCallback(
    (tarifIndex: number) => {
      const periods = tarifs[tarifIndex].periods;
      const pricePartitions = tarifs[tarifIndex].pricePartitions;
      append({
        name:
          tarifs && tarifs[tarifIndex].name
            ? `${tarifs[tarifIndex].name} (${t('common.copy')})`
            : t('carRental.categoryForm.tarif', {
                index: fields.length + 1,
              }),
        periods: periods,
        pricePartitions: pricePartitions,
      } as FieldArray<FieldsType, ArrayPath<FieldsType>>[][0]);
    },
    [append, fields.length, tarifs, t],
  );

  return (
    <>
      {fields.map((item, tarifIndex) => (
        <CategoryTarifCard
          key={item.id}
          control={control}
          categoryIndex={index}
          tarifIndex={tarifIndex}
          deleteTarif={() => {
            if (fields.length > 1) {
              remove(tarifIndex);
              return;
            }
            const resetTarif = createDefaultTarif([]);
            replace([resetTarif]);
            resetField(`carCategories.${index}.tarifs.0` as Path<FieldsType>, {
              defaultValue: resetTarif as PathValue<
                FieldsType,
                Path<FieldsType>
              >,
            });
          }}
          duplicateTarif={() => appendTarif(tarifIndex)}
          isViewMode={isViewMode}
        />
      ))}
      {!isViewMode && (
        <StyledButton
          className="text-brand-600 w-fit"
          title={t('carRental.categoryForm.addTarif')}
          onClick={() => append(createDefaultTarif())}
          variant="outlined"
          icon={<PlusIcon className="size-5 fill-brand-600" />}
        />
      )}
    </>
  );
};

export default CategoryTarifList;

interface CategoryTarifCardProps<
  FieldsType extends ExtendedCarCategoriesFormData,
> {
  control: Control<FieldsType>;
  categoryIndex: number;
  tarifIndex: number;
  deleteTarif: () => void;
  duplicateTarif: () => void;
  isViewMode?: boolean;
}

const CategoryTarifCard = <FieldsType extends ExtendedCarCategoriesFormData>({
  control,
  categoryIndex,
  tarifIndex,
  deleteTarif,
  duplicateTarif,
  isViewMode = false,
}: CategoryTarifCardProps<FieldsType>) => {
  const { t } = useTranslation();
  const [isFolded, setIsFolded] = useState(false);
  const currentTitle = useWatch({
    control,
    name: `carCategories.${categoryIndex}.tarifs.${tarifIndex}.name` as Path<FieldsType>,
  });

  const tarifData = useWatch({
    control,
    name: `carCategories.${categoryIndex}.tarifs.${tarifIndex}` as Path<FieldsType>,
  });

  const { error } = TarificationSchema.safeParse(tarifData);

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row justify-between">
        <CardTitle
          className="text-base leading-6 hover:cursor-pointer"
          onClick={() => setIsFolded(!isFolded)}
        >
          {(currentTitle as string) ||
            t('carRental.categoryForm.tarif', { index: tarifIndex + 1 })}
        </CardTitle>
        <div className="flex gap-10">
          <div className="flex gap-4">
            {!isViewMode && (
              <StyledTooltipContainer
                tooltipMessage={t('carRental.categoryForm.duplicate')}
              >
                <CopyIcon
                  className="size-5 stroke-brand-600 hover:cursor-pointer"
                  onClick={() => {
                    setIsFolded(true);
                    duplicateTarif();
                  }}
                />
              </StyledTooltipContainer>
            )}
            {!isViewMode && (
              <StyledTooltipContainer
                tooltipMessage={t('carRental.categoryForm.delete')}
              >
                <TrashIcon
                  className="size-5 stroke-brand-600 hover:cursor-pointer"
                  onClick={() => deleteTarif()}
                />
              </StyledTooltipContainer>
            )}
          </div>
          {isFolded ? (
            <ChevronDown
              className="size-5 hover:cursor-pointer"
              onClick={() => setIsFolded(false)}
            />
          ) : (
            <ChevronUp
              className="size-5 hover:cursor-pointer"
              onClick={() => setIsFolded(true)}
            />
          )}
        </div>
      </CardHeader>
      {!isFolded && (
        <CardContent className="flex flex-col gap-6">
          <div className="flex flex-col">
            {error && !isViewMode && (
              <>
                {error.issues
                  .filter(({ code }) => code === 'custom')
                  .map(({ message }, indexError) => (
                    <p
                      className="text-sm leading-6 font-medium text-error-500"
                      key={`error-${indexError}`}
                    >
                      {message}
                    </p>
                  ))}
              </>
            )}
          </div>
          <div className="flex flex-col gap-4">
            <StyledInput
              control={control}
              name={
                `carCategories.${categoryIndex}.tarifs.${tarifIndex}.name` as Path<FieldsType>
              }
              placeholder={t('carRental.categoryForm.pricePlaceholder')}
              disabled={isViewMode}
            />
            <CategoryNestedTarifPeriods
              control={control}
              categoryIndex={categoryIndex}
              tarifIndex={tarifIndex}
              isViewMode={isViewMode}
            />
            <CategoryNestedTarifRepartition
              control={control}
              categoryIndex={categoryIndex}
              tarifIndex={tarifIndex}
              isViewMode={isViewMode}
            />
          </div>
        </CardContent>
      )}
    </Card>
  );
};

interface CategoryNestedTarifPeriodsProps<
  FieldsType extends ExtendedCarCategoriesFormData,
> {
  control: Control<FieldsType>;
  categoryIndex: number;
  tarifIndex: number;
  isViewMode?: boolean;
}

const CategoryNestedTarifPeriods = <
  FieldsType extends ExtendedCarCategoriesFormData,
>({
  control,
  categoryIndex,
  tarifIndex,
  isViewMode = false,
}: CategoryNestedTarifPeriodsProps<FieldsType>) => {
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const fieldName =
    `carCategories.${categoryIndex}.tarifs.${tarifIndex}.periods` as ArrayPath<FieldsType>;

  const { fields, remove, append } = useFieldArray({
    control,
    name: fieldName,
  });

  const firstPeriodApplicationDays = useWatch({
    control,
    name: `carCategories.${categoryIndex}.tarifs.${tarifIndex}.periods.0.applicationDays` as Path<FieldsType>,
  });

  const { watch } = useFormContext();
  const periodValues = watch(fieldName);

  const getStartDateValue = (index: number, add = 0) => {
    return periodValues && periodValues[index]?.startDate
      ? addDays(new Date(periodValues[index]?.startDate), add)
      : undefined;
  };

  const getEndDateValue = (index: number, add = 0) => {
    return periodValues && periodValues[index]?.endDate
      ? addDays(new Date(periodValues[index]?.endDate), add)
      : undefined;
  };

  return (
    <>
      {fields.map((item, index) => (
        <div className="mb-1" key={item.id}>
          <div
            className={clsx('flex flex-col', {
              'w-full gap-1.5': !isViewMode,
              'lg:flex-row md:justify-between gap-6': isViewMode,
            })}
          >
            <div className={clsx({ 'flex flex-col w-full': isViewMode })}>
              <div
                className={clsx({
                  'flex justify-between': !isViewMode,
                })}
              >
                <Label className="text-gray-700">
                  {t('carRental.categoryForm.period', {
                    periodIndex: index + 1,
                    tarifIndex: tarifIndex + 1,
                  })}
                </Label>
                {fields.length > 1 && !isViewMode && (
                  <StyledButton
                    title={t('carRental.categoryForm.delete')}
                    className="p-0 h-auto"
                    variant="link"
                    onClick={() => remove(index)}
                  />
                )}
              </div>
              <div
                className={clsx('flex items-center gap-2', {
                  'flex-col md:flex-row w-full': !isViewMode,
                })}
              >
                <span>{t('carRental.categoryForm.from')}</span>
                <StyledDatePicker
                  control={control}
                  name={`${fieldName}.${index}.startDate` as Path<FieldsType>}
                  className="w-full"
                  isViewMode={isViewMode}
                  earliestDate={getEndDateValue(index - 1, 1)}
                  enforceCurrentDateBoundary={false}
                />
                <span>{t('carRental.categoryForm.to')}</span>
                <StyledDatePicker
                  control={control}
                  name={`${fieldName}.${index}.endDate` as Path<FieldsType>}
                  className="w-full"
                  isViewMode={isViewMode}
                  earliestDate={getStartDateValue(index, 0)}
                  enforceCurrentDateBoundary={false}
                />
              </div>
            </div>
            <div className="flex flex-col w-full gap-1.5">
              <Label className="text-gray-700">
                {t('carRental.categoryForm.validDays')}
              </Label>
              <StyledCheckboxGroup
                control={control}
                name={
                  `${fieldName}.${index}.applicationDays` as Path<FieldsType>
                }
                options={WEEKDAYS.map((weekday) => ({
                  value: weekday,
                  label: getTranslatedConst(WEEKDAYS_TRANSLATED)[weekday][0],
                }))}
                isViewMode={isViewMode}
              />
            </div>
          </div>
        </div>
      ))}
      {!isViewMode && (
        <StyledButton
          className="text-brand-600 w-fit pl-0"
          title={t('carRental.categoryForm.addPeriod')}
          onClick={() => {
            append({
              startDate: undefined,
              endDate: undefined,
              applicationDays: firstPeriodApplicationDays,
            } as FieldArray<FieldsType, ArrayPath<FieldsType>>[][0]);
          }}
          variant="link"
          icon={<PlusIcon className="size-5 fill-brand-600" />}
        />
      )}
    </>
  );
};

interface CategoryNestedTarifRepartitionProps<
  FieldsType extends ExtendedCarCategoriesFormData,
> {
  control: Control<FieldsType>;
  categoryIndex: number;
  tarifIndex: number;
  isViewMode?: boolean;
}

const CategoryNestedTarifRepartition = <
  FieldsType extends ExtendedCarCategoriesFormData,
>({
  control,
  categoryIndex,
  tarifIndex,
  isViewMode = false,
}: CategoryNestedTarifRepartitionProps<FieldsType>) => {
  const { t } = useTranslation();
  const fieldName =
    `carCategories.${categoryIndex}.tarifs.${tarifIndex}.pricePartitions` as ArrayPath<FieldsType>;

  const { fields, remove, append } = useFieldArray({
    control,
    name: fieldName,
  });
  const { watch } = useFormContext();

  const repartitionValues = watch(fieldName);

  const getToValue = (index: number, add = 0) => {
    return repartitionValues && repartitionValues[index]?.to
      ? Number(repartitionValues[index]?.to) + add
      : undefined;
  };

  const getFromValue = (index: number, add = 0) => {
    return repartitionValues && repartitionValues[index]?.from
      ? Number(repartitionValues[index]?.from) + add
      : undefined;
  };

  return (
    <div className="flex flex-col gap-2">
      {fields.map((item, index) => (
        <div className="flex flex-col items-end" key={item.id}>
          {fields.length > 1 && !isViewMode && (
            <StyledButton
              title={t('carRental.categoryForm.delete')}
              className="p-0 h-auto"
              variant="link"
              onClick={() => remove(index)}
            />
          )}
          <div className="flex justify-between w-full flex-wrap md:flex-nowrap">
            <div className="flex flex-col w-full gap-1.5">
              <Label className="text-gray-700">
                {t('carRental.categoryForm.duration')}
              </Label>
              <div className="flex gap-2 items-center flex-wrap md:flex-nowrap">
                <Label className="text-gray-700">
                  {t('carRental.categoryForm.fromDay')}
                </Label>
                <div className="w-16">
                  <StyledInput
                    control={control}
                    type="number"
                    name={`${fieldName}.${index}.from` as Path<FieldsType>}
                    min={getToValue(index - 1) || 1}
                    max={getToValue(index, -1)}
                    disabled={isViewMode}
                  />
                </div>
                {t('carRental.categoryForm.days')}
                <Label className="text-gray-700">
                  {t('carRental.categoryForm.toDay')}
                </Label>
                <div className="w-16">
                  <StyledInput
                    control={control}
                    type="number"
                    name={`${fieldName}.${index}.to` as Path<FieldsType>}
                    min={getFromValue(index) || 1}
                    max={getFromValue(index + 1, -1)}
                    disabled={isViewMode}
                  />
                </div>
                {t('carRental.categoryForm.days')}
              </div>
            </div>
            <div className="flex gap-2">
              <StyledInput
                control={control}
                type="number"
                min={0}
                name={`${fieldName}.${index}.price` as Path<FieldsType>}
                title={t('carRental.categoryForm.pricePerDay')}
                placeholder={t('carRental.categoryForm.pricePlaceholder')}
                disabled={isViewMode}
              />
            </div>
          </div>
        </div>
      ))}
      {!isViewMode && (
        <StyledButton
          className="text-brand-600 w-fit pl-0"
          title={t('carRental.categoryForm.addDuration')}
          onClick={() => {
            const lastToValue = getToValue(fields.length - 1, 1);
            return append({
              price: undefined,
              from: lastToValue,
              to: lastToValue ? lastToValue + 1 : 1,
            } as FieldArray<FieldsType, ArrayPath<FieldsType>>[][0]);
          }}
          variant="link"
          icon={<PlusIcon className="size-5 fill-brand-600" />}
        />
      )}
    </div>
  );
};
